import {
  faArrowUpRightFromSquare,
  faMagnifyingGlass,
  faRefresh,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { Root as AccordionRoot } from "@radix-ui/react-accordion";
import { useQueryClient } from "@tanstack/react-query";
import { range } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import { twJoin, twMerge } from "tailwind-merge";
import { useSessionStorage } from "usehooks-ts";
import { validate as isValidUUID } from "uuid";

import {
  DecisionHistoryRecordV3,
  decisionsV3BaseKey,
  DevisionHistoryV3GetRequest,
  NO_DECISION_FOUND,
  SingleDecisionEnvelope,
  useHistoryDecisionsV3,
  useHistoryDecisionV3,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { GroupFilter } from "src/api/endpoints";
import { FlowGroup, FlowGroupForCount, Flow } from "src/api/flowTypes";
import { useFlow, useFlows } from "src/api/queries";
import { DecisionEnvironment, DecisionSummaryResponse } from "src/api/types";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { EditorAccordionItem } from "src/base-components/EditorAccordionItem";
import {
  EnvironmentPill,
  EnvironmentPillPlaceholder,
} from "src/base-components/EnvironmentPill";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Icon } from "src/base-components/Icon";
import { LoadingView } from "src/base-components/LoadingView";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { HistoricalDecisionV3 } from "src/clients/history-v3";
import { ColumnSelector } from "src/decisionsOverview/ColumnSelector";
import { DecisionsSidepaneWrapper } from "src/decisionsOverview/DecisionsSidepaneWrapper";
import { LinkedFlowsPill } from "src/decisionsOverview/LinkedFlowsPill";
import { useDecisionsOverview } from "src/decisionsOverview/queries";
import {
  useDecisionEnv,
  useDetermineDecisionEnvironment,
} from "src/decisionsOverview/useDetermineDecisionEnvironment";
import { useViewOptions } from "src/decisionsOverview/useViewOptions";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { useSidepaneContext } from "src/entities/entityView/DecisionsSidePane/SidePaneContext";
import { ManualReviewFilters } from "src/entities/entityView/DecisionsTab/ManualReviewStatusFilter";
import { SubHeader } from "src/flow/SubHeader";
import { TracingHoverBehavior } from "src/flow/decisionHistory/Columns";
import { DecisionHistoryTable } from "src/flow/decisionHistory/TableWrapper";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { DashboardContent } from "src/layout/DashboardContent";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { URLKeys } from "src/router/SearchParams";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  DashboardPageParams,
  getBaseUrl,
  getFlowSubpageUrl,
  getUrlToWsDashboard,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { isFlowGroup, isFlowGroupForCount } from "src/utils/predicates";
import { pluralize } from "src/utils/stringUtils";
import { StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const sidepanePlaceholderPadding = 36;

export const useDecisionsOverviewQueryParams = () => {
  const [searchParams] = useSearchParams();

  return {
    entityId: searchParams.get(URLKeys.EntityId),
    decisionId: searchParams.get(URLKeys.DecisionId),
  };
};

const RightContent: React.FC<{
  isOpen: boolean;
  count: number | undefined;
  flow: Flow;
  includeEntityId: boolean;
}> = ({ isOpen, count, flow, includeEntityId }) => {
  const { value, isSaving, canSelect, canDeselect, onChange, onReset, onSave } =
    useViewOptions({
      flow,
      includeEntityId,
    });
  return (
    <div
      className={twJoin(
        "flex h-13 items-center gap-x-2 border border-l-0 border-gray-200 bg-white px-6",
        isOpen ? "rounded-tr-lg" : "rounded-r-lg",
      )}
    >
      <Pill dataLoc={`decisions-count-${count}`} size="sm" variant="gray">
        <Pill.Text>
          {count !== undefined ? (
            pluralize(count, "Decision")
          ) : (
            <>&nbsp;&nbsp; Decisions</>
          )}
        </Pill.Text>
      </Pill>
      <ColumnSelector
        canDeselect={canDeselect}
        canSelect={canSelect}
        flow={flow}
        includeEntityId={includeEntityId}
        isSaving={isSaving}
        value={value}
        onChange={onChange}
        onReset={onReset}
        onSave={onSave}
      />
    </div>
  );
};

type ItemProps = {
  isOpen: boolean;
  flow: Flow;
  title: string;
  includeRelatedEntityDecisions?: boolean;
  manualReviewFilter?: ManualReviewFilters;
  count: number | undefined;
  selectedDecision: HistoricalDecisionV3 | null;
  singleDecision: HistoricalDecisionV3 | null;
  topLevelFilters: TopLevelFilters;
  filter: DevisionHistoryV3GetRequest | undefined;
  rowBehavior?: TracingHoverBehavior;
  onFilterChange: (tableFilter: DevisionHistoryV3GetRequest) => void;
  onSelectDecision: (decision: HistoricalDecisionV3 | null) => void;
};

const buildQueryFilter = (
  filter: TopLevelFilters,
  fields: DevisionHistoryV3GetRequest | undefined,
): DevisionHistoryV3GetRequest => {
  return {
    startStartTime: filter.timeWindow?.from.toISOString(),
    endStartTime: filter.timeWindow?.to.toISOString(),
    environments: filter.decisionEnv,
    ...(filter.entityId && { entityIds: filter.entityId }),
    ...(filter.decisionId &&
      isValidUUID(filter.decisionId) && {
        parentDecisionIds: filter.decisionId,
      }),
    ...fields,
  };
};

const FlowAccordionItem: React.FC<ItemProps> = ({
  isOpen,
  includeRelatedEntityDecisions,
  manualReviewFilter,
  flow: flowShallow,
  title,
  count,
  topLevelFilters,
  filter,
  singleDecision,
  selectedDecision,
  rowBehavior,
  onSelectDecision,
  onFilterChange,
}) => {
  const [cancellingDecisionIds, setCancellingDecisionIds] = useState<string[]>(
    [],
  );
  const userId = useCurrentUserId();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const flow = useFlow(flowShallow?.id);
  const { columnVisibility, fieldsMask } = useViewOptions({
    flow: flow.data,
    includeCases: includeRelatedEntityDecisions,
    includeEntityId: !includeRelatedEntityDecisions,
  });
  const { workspace } = useWorkspaceContext();

  const decisions = useHistoryDecisionsV3({
    baseUrl: workspace?.base_url ?? "",
    flowIds: flowShallow?.id,
    ...buildQueryFilter(topLevelFilters, filter),
    entityIds: includeRelatedEntityDecisions
      ? undefined
      : (topLevelFilters.entityId ?? undefined),
    relatedEntityIds: includeRelatedEntityDecisions
      ? (topLevelFilters.entityId ?? undefined)
      : undefined,
    enabled: flowShallow?.slug !== undefined,
    caseAssignees:
      manualReviewFilter === ManualReviewFilters.ASSIGNED_TO_ME
        ? userId
        : undefined,
    caseStatus:
      manualReviewFilter === ManualReviewFilters.NEEDS_REVIEW
        ? "needs_review,in_progress"
        : undefined,
    $fields: fieldsMask,
  });

  if (!flow.data) return <AccordionSkeletonItem key={flowShallow?.id} />;

  const linkToFlow =
    getBaseUrl() + getFlowSubpageUrl(orgId, wsId, flow.data.id, "history");

  return (
    <EditorAccordionItem
      className="decideScrollbar ml-11 mr-4 overflow-hidden overflow-x-auto pl-0 pr-0 pt-4"
      contentWrapperClassName="border-gray-200 border border-t-0 bg-white rounded-b-lg"
      dataLoc={`accordion-item-${flow.data.name}`}
      headerClassName={twJoin(
        "flex-grow border border-r-0 border-gray-200 bg-white",
        isOpen ? "rounded-tl-lg" : "rounded-l-lg",
      )}
      headerContent={
        <RightContent
          count={count}
          flow={flow.data}
          includeEntityId={!includeRelatedEntityDecisions}
          isOpen={isOpen}
        />
      }
      title={
        <div
          className="flex items-center gap-x-1.5"
          data-loc="accordion-item-title"
        >
          <Tooltip title={title} asChild>
            <span className="min-w-24 line-clamp-2 text-left">{title}</span>
          </Tooltip>
          <Tooltip placement="right" title="Open in new tab" asChild>
            <div>
              <Icon
                color="text-gray-800"
                icon={faArrowUpRightFromSquare}
                size="2xs"
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(linkToFlow, "_blank");
                }}
              />
            </div>
          </Tooltip>
          <LinkedFlowsPill
            childFlows={flowShallow.child_flows}
            parentFlows={flowShallow.parent_flows}
          />
        </div>
      }
      value={flow.data.id}
      headerAsChild
    >
      <div className="relative mx-auto flex max-h-[640px]">
        <FloatingWindowsProvider>
          <DecisionHistoryTable
            canFetchNextPage={
              !singleDecision &&
              Boolean(!decisions.isFetching && decisions.hasNextPage)
            }
            cancellingDecisionIds={cancellingDecisionIds}
            changeFilterFields={onFilterChange}
            columnVisibility={columnVisibility}
            decisionHistory={
              singleDecision
                ? [singleDecision]
                : decisions.data?.pages.flatMap((page) => page.decisions)
            }
            env={topLevelFilters.decisionEnv}
            fetchNextPage={decisions.fetchNextPage}
            filterFields={filter || {}}
            flow={flow.data}
            includeEntityId={!includeRelatedEntityDecisions}
            isFetching={!singleDecision && decisions.isLoading}
            selectedDecisionId={
              selectedDecision ? selectedDecision.id : undefined
            }
            setCancellingDecisionIds={setCancellingDecisionIds}
            tracingHoverBehavior={rowBehavior}
            includeInputOutput
            includeOutcomes
            onDecisionClick={{
              predicate: () => true,
              effect: onSelectDecision,
            }}
          />
        </FloatingWindowsProvider>
      </div>
    </EditorAccordionItem>
  );
};

type Props = {
  singleDecision: HistoricalDecisionV3 | null;
  includeRelatedEntityDecisions?: boolean;
  manualReviewFilter?: ManualReviewFilters;
  flowGroups: FlowGroup[];
  flowGroupsForCount: FlowGroupForCount[] | undefined;
  selectedDecision: HistoricalDecisionV3 | null;
  topLevelFilters: TopLevelFilters;
  groupFilter: GroupFilter;
  rowBehavior?: TracingHoverBehavior;
  onGroupFilterChange: React.Dispatch<React.SetStateAction<GroupFilter>>;
  onSelectDecision: (decision: HistoricalDecisionV3 | null) => void;
};

export const FlowAccordion: React.FC<Props> = ({
  singleDecision,
  flowGroups,
  flowGroupsForCount,
  topLevelFilters,
  selectedDecision,
  groupFilter,
  rowBehavior,
  includeRelatedEntityDecisions,
  manualReviewFilter,
  onGroupFilterChange,
  onSelectDecision,
}) => {
  const { decisionId: urlDecisionId, entityId: urlEntityId } =
    useDecisionsOverviewQueryParams();
  const [accordionValue, setAccordionValue] = useSessionStorage<string[]>(
    `decisions-overview-accordion-search-id-${urlDecisionId ?? urlEntityId}`,
    [],
  );
  const [hasSetInitialValue, setHasSetInitialValue] =
    useSessionStorage<boolean>(
      `decisions-overview-accordion-initial-set-${urlDecisionId ?? urlEntityId}`,
      false,
    );

  useEffect(() => {
    onSelectDecision(null);
  }, [topLevelFilters, groupFilter, onSelectDecision]);

  useEffect(() => {
    if (flowGroups.length > 0 && !hasSetInitialValue) {
      setAccordionValue([flowGroups[0].flow.id]);
      setHasSetInitialValue(true);
    }
  }, [
    flowGroups,
    hasSetInitialValue,
    setAccordionValue,
    setHasSetInitialValue,
  ]);

  const handleAccordionChange = (value: string[]) => {
    if (
      selectedDecision?.flow?.id &&
      !value.includes(selectedDecision.flow.id)
    ) {
      onSelectDecision(null);
    }
    setAccordionValue(value);
  };

  return (
    <AccordionRoot
      className="flex flex-col gap-y-3 pb-6"
      type="multiple"
      value={accordionValue}
      onValueChange={handleAccordionChange}
    >
      {flowGroups.map((group) => (
        <FlowAccordionItem
          key={group.flow.id}
          count={
            flowGroupsForCount
              ? (flowGroupsForCount.find(
                  (flowGroup) => flowGroup.flowId === group.flow.id,
                )?.count ?? 0)
              : undefined
          }
          filter={groupFilter[group.flow.id]}
          flow={group.flow}
          includeRelatedEntityDecisions={includeRelatedEntityDecisions}
          isOpen={accordionValue.includes(group.flow.id)}
          manualReviewFilter={manualReviewFilter}
          rowBehavior={rowBehavior}
          selectedDecision={selectedDecision}
          singleDecision={
            singleDecision?.flow.id === group.flow.id ? singleDecision : null
          }
          title={group.flow.name}
          topLevelFilters={topLevelFilters}
          onFilterChange={(tableFilter: DevisionHistoryV3GetRequest) => {
            onGroupFilterChange((prevGroupFilter) => ({
              ...prevGroupFilter,
              [group.flow.id]: tableFilter,
            }));
          }}
          onSelectDecision={onSelectDecision}
        />
      ))}
    </AccordionRoot>
  );
};

export type TopLevelFilters = {
  decisionEnv: DecisionEnvironment;
  entityId: string | null;
  decisionId: string | null;
  timeWindow: StrictDateRange | undefined;
};

const AccordionSkeletonItem: React.FC = () => {
  return (
    <EditorAccordionItem
      headerClassName="border-gray-200 border flex-grow bg-white rounded-lg"
      title={<SkeletonPlaceholder width="w-96" />}
      value="dummy"
    >
      <></>
    </EditorAccordionItem>
  );
};

export const AccordionSkeleton: React.FC = () => {
  return (
    <AccordionRoot
      className="flex flex-col gap-y-3"
      defaultValue={[]}
      type="multiple"
    >
      {range(4).map((index) => (
        <AccordionSkeletonItem key={index} />
      ))}
    </AccordionRoot>
  );
};

export const DecisionsOverview: React.FC = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const { showOmnibox } = useOmniboxActions();
  const { workspace } = useWorkspaceContext();
  const { entityId, decisionId } = useDecisionsOverviewQueryParams();

  const {
    decisionEnvParam,
    decisionEnvForQueries,
    confirmedNoLiveDecisions,
    setDecisionEnvParam,
    setConfirmedNoLiveDecisions,
  } = useDecisionEnv();

  const [selectedDecision, state_setSelectedDecision] =
    useState<HistoricalDecisionV3 | null>(null);

  const setSelectedDecision = useCallback(
    (decision: HistoricalDecisionV3 | null) => {
      state_setSelectedDecision(decision);
      if (decision !== null) {
        tracker.emit(
          trackingEvents.openDecisionDetails({
            query_param_decision_id: decisionId,
            query_param_entity_id: entityId,
            decision_id: decision.id,
            organization_id: orgId,
            env: decisionEnvParam,
          }),
        );
      }
    },
    [decisionEnvParam, decisionId, entityId, orgId],
  );

  const {
    dateRangePickerValue,
    onDateRangePickerChange,
    onDateRangePickerReset,
    timeWindow,
  } = useTimeWindow<StrictDateRange | undefined>(
    undefined,
    "decisions-overview-time-window",
  );

  const topLevelFilters: TopLevelFilters = useMemo(() => {
    return {
      decisionEnv: decisionEnvForQueries,
      entityId: entityId,
      decisionId: decisionId,
      timeWindow,
    };
  }, [decisionEnvForQueries, entityId, decisionId, timeWindow]);

  const [groupFilter, setGroupFilter] = useSessionStorage<GroupFilter>(
    "decisions-overview-group-filter",
    {},
  );
  const queryClient = useQueryClient();

  const decisionsOverviewQuery = useDecisionsOverview(workspace.base_url, {
    environments: decisionEnvForQueries,
    groupby: "flow.id",
    aggregate: "count",
    entity_id: entityId ?? undefined,
    parent_decision_id: decisionId ?? undefined,
    timeWindow: timeWindow,
  });

  const decisionsOverviewQueryForCount = useDecisionsOverview(
    workspace.base_url,
    {
      environments: decisionEnvForQueries,
      groupby: "flow.id",
      aggregate: "count",
      entity_id: entityId ?? undefined,
      parent_decision_id: decisionId ?? undefined,
      timeWindow: timeWindow,
      groupfilter: groupFilter,
    },
  );

  const singleDecisionQuery = useHistoryDecisionV3({
    baseUrl: workspace.base_url ?? "",
    decisionId: decisionId ?? "",
    environmentToMatch: decisionEnvForQueries,
    // We give an initial value for the deactivated state so that the loading view treats this as loaded.
    initialData: decisionId
      ? undefined
      : { isQueryError: true, reason: NO_DECISION_FOUND },
  });

  const getDatePickerValue = (): DateRange | undefined => {
    if (!decisionId) {
      return dateRangePickerValue;
    }

    if (
      !singleDecisionQuery.data?.isQueryError &&
      singleDecisionQuery.data?.decision?.start_time
    ) {
      return {
        from: new Date(singleDecisionQuery?.data?.decision?.start_time),
      };
    }

    return undefined;
  };

  const flowsQuery = useFlows({ workspaceId: wsId });

  const flowGroupsForCount: FlowGroupForCount[] | undefined = useMemo(() => {
    const groups = decisionsOverviewQueryForCount.data?.groups
      .map((group) => ({
        flowId: group.flow?.id,
        count: group.count,
      }))
      .filter(isFlowGroupForCount);
    const singleDecision = singleDecisionQuery.data;
    if (
      singleDecision &&
      !singleDecision.isQueryError &&
      singleDecision.decision.flow.id
    ) {
      const existingGroup = groups?.find(
        (group) => group.flowId === singleDecision.decision.flow.id,
      );
      if (existingGroup) {
        existingGroup.count += 1;
      } else {
        groups?.push({
          flowId: singleDecision.decision.flow.id,
          count: 1,
        });
      }
    }
    return groups;
  }, [decisionsOverviewQueryForCount.data, singleDecisionQuery.data]);

  const handleRefresh = () => {
    decisionsOverviewQuery.refetch();
    decisionsOverviewQueryForCount.refetch();
    queryClient.refetchQueries({ queryKey: decisionsV3BaseKey });
  };

  useDetermineDecisionEnvironment({
    decisionEnvParam,
    entityId,
    decisionsOverview: decisionsOverviewQuery.data,
    setDecisionEnvParam,
    decisionId,
    singleDecision: singleDecisionQuery.data,
    confirmedNoLiveDecisions,
    setConfirmedNoLiveDecisions,
  });
  const { width, isResizing } = useSidepaneContext();

  const getTitle = () => {
    if (decisionId) {
      return "Decision ID";
    }

    if (entityId) {
      return "Decisions for Entity ID";
    }

    return "Decisions";
  };

  return (
    <DashboardContent
      Header={
        <SubHeader
          backTo={getUrlToWsDashboard({ orgId, wsId })}
          title={
            <>
              <span className="mr-2 whitespace-nowrap align-middle">
                {getTitle()}
              </span>
              <div className="mb-0.5 inline-block h-fit max-w-60 items-center overflow-hidden align-middle">
                <Pill
                  dataLoc="decisions-overview-id-pill"
                  size="sm"
                  variant="gray"
                  maxWidth
                >
                  <Pill.Text fontType="code">
                    {decisionId ?? entityId ?? "-"}
                  </Pill.Text>
                </Pill>
              </div>
            </>
          }
          titleAction={
            <>
              <CopyTextIcon
                tooltip="Copy ID"
                value={decisionId ?? entityId ?? ""}
              />
              {decisionEnvParam === null ? (
                <EnvironmentPillPlaceholder />
              ) : (
                <EnvironmentPill
                  value={decisionEnvParam}
                  onChange={setDecisionEnvParam}
                />
              )}
            </>
          }
        >
          <SubHeader.Button
            dataLoc="header-search"
            icon={faMagnifyingGlass}
            tooltip="Search"
            onClick={showOmnibox}
          />
          <SubHeader.DatePicker
            disabled={Boolean(decisionId)}
            placeholder="All time"
            value={getDatePickerValue()}
            resetable
            onChange={onDateRangePickerChange}
            onReset={onDateRangePickerReset}
          />
          <SubHeader.Button
            dataLoc="refresh-decisions"
            disabled={decisionsOverviewQuery.isRefetching}
            icon={faRefresh}
            spin={decisionsOverviewQuery.isRefetching}
            tooltip="Refresh"
            onClick={handleRefresh}
          />
        </SubHeader>
      }
    >
      <div
        className={twMerge(
          "flex h-full w-full",
          isResizing && "cursor-col-resize",
        )}
      >
        <div className="mx-7 mb-10 h-fit min-w-[600px] grow overflow-hidden">
          <LoadingView
            queryResult={[
              decisionsOverviewQuery,
              flowsQuery,
              singleDecisionQuery,
            ]}
            renderUpdated={([decisionSummary, flows, singleDecision]: [
              DecisionSummaryResponse,
              Flow[],
              SingleDecisionEnvelope<DecisionHistoryRecordV3>,
            ]) => {
              const flowGroups: FlowGroup[] =
                decisionSummary.groups
                  .map((group) => ({
                    flow: flows.find((flow) => flow.id === group.flow?.id),
                  }))
                  .filter(isFlowGroup) ?? [];
              if (
                !singleDecision.isQueryError &&
                !flowGroups.find(
                  (group) => group.flow?.id === singleDecision.decision.flow.id,
                )
              ) {
                const singleDecisionFlow = flows.find(
                  (flow) => flow.id === singleDecision.decision.flow.id,
                );
                if (singleDecisionFlow) {
                  flowGroups.unshift({
                    flow: singleDecisionFlow,
                  });
                }
              }
              if (flowGroups.length === 0) {
                return (
                  <div className="h-[calc(100vh-120px)]">
                    <EmptyState
                      description="Try adjusting the search or filter to find what you are looking for"
                      headline="No results found"
                      icon={faSearch}
                    />
                  </div>
                );
              }
              return (
                <FlowAccordion
                  flowGroups={flowGroups.sort((a, b) =>
                    a.flow?.name.localeCompare(b.flow?.name),
                  )}
                  flowGroupsForCount={flowGroupsForCount}
                  groupFilter={groupFilter}
                  selectedDecision={selectedDecision}
                  singleDecision={
                    singleDecision.isQueryError ? null : singleDecision.decision
                  }
                  topLevelFilters={topLevelFilters}
                  onGroupFilterChange={setGroupFilter}
                  onSelectDecision={setSelectedDecision}
                />
              );
            }}
            renderUpdating={() => <AccordionSkeleton />}
          />
        </div>
        {/*Make some space for the sidepane to take, but allow it to overlap the
        table on small screens. */}
        {!!selectedDecision && (
          <div style={{ width: width - sidepanePlaceholderPadding }} />
        )}
        <DecisionsSidepaneWrapper
          baseUrl={workspace.base_url!}
          decision={selectedDecision}
          isOpen={!!selectedDecision}
          onClose={() => setSelectedDecision(null)}
        />
      </div>
      <div className="h-14" />
    </DashboardContent>
  );
};
